import { isAxiosError } from 'axios'
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  Zap,
  Mail,
  StickyNote,
  PenLine,
  AlertCircle,
  CheckCircle2,
  Edit3,
  Power,
  PowerOff,
  ChevronRight,
  RefreshCw,
  Settings2,
  Hash,
  Bell,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { readAutomationPrefill } from '@/features/automations/automationPrefill'
import { EmailDeliveryFields } from '@/features/automations/components/EmailDeliveryFields'
import { SendNotificationFields, validateSendNotification } from '@/features/automations/components/SendNotificationFields'
import {
  AvailableVariables,
  ButtonTriggerFilterPicker,
  FieldTriggerFilterPicker,
  TagTriggerFilterPicker,
} from '@/features/automations/components/TriggerFilterPickers'
import { CallTriggerFilterPicker } from '@/features/calling/components/CallTriggerFilterPicker'
import { useAutomations, useAutomationDetail } from '@/hooks/useAutomations'
import { useCallingMe } from '@/hooks/useCalling'
import { useAllowedActions } from '@/hooks/usePermission'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { AutomationAction, AutomationTriggerFilter, UpdateAutomationRequest } from '@/types/api/automation'
import { ActionType, AutomationActionType } from '@/types/enums'
import { cn } from '@/utils/cn'

/** The problem-detail message of a failed API call, if there is one. */
function apiErrorDetail(err: unknown): string | undefined {
  return isAxiosError(err) ? (err.response?.data as { detail?: string } | undefined)?.detail : undefined
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface ActionMetadata {
  note?: string
  to?: string
  subject?: string
  body?: string
  fieldKey?: string
  value?: string
  // SEND_NOTIFICATION
  channels?: string[]
  title?: string
  recipientRoleIds?: string[]
  recipientUserIds?: string[]
  notifyRecordCreator?: boolean
  notifyActor?: boolean
  // SEND_EMAIL reply, sender, draft, cc/bcc and attachments (see EmailDeliveryFields)
  replyToField?: string
  replyTarget?: string
  sendFrom?: string
  sendFromConnectionId?: string
  deliveryMode?: string
  cc?: string
  bcc?: string
  attachmentFields?: string[]
}

interface FormAction {
  id: string | null
  type: AutomationActionType
  metadata: ActionMetadata
}

interface FormErrors {
  recordGroupId?: string
  actions?: { [index: number]: { [field: string]: string } }
}

// ─── Config ──────────────────────────────────────────────────────────────────

const ACTION_CONFIG: Record<
  AutomationActionType,
  {
    label: string
    icon: React.FC<{ className?: string }>
    color: string
    bgColor: string
    borderColor: string
    gradientFrom: string
    gradientTo: string
  }
> = {
  [AutomationActionType.ADD_NOTE]: {
    label: 'Add Note',
    icon: StickyNote,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    gradientFrom: 'from-amber-500',
    gradientTo: 'to-orange-400',
  },
  [AutomationActionType.SEND_EMAIL]: {
    label: 'Send Email',
    icon: Mail,
    color: 'text-primary-700',
    bgColor: 'bg-primary-50',
    borderColor: 'border-primary-200',
    gradientFrom: 'from-primary-500',
    gradientTo: 'to-indigo-400',
  },
  [AutomationActionType.UPDATE_FIELD]: {
    label: 'Update Field',
    icon: PenLine,
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    gradientFrom: 'from-emerald-500',
    gradientTo: 'to-teal-400',
  },
  [AutomationActionType.SEND_NOTIFICATION]: {
    label: 'Send Notification',
    icon: Bell,
    color: 'text-fuchsia-700',
    bgColor: 'bg-fuchsia-50',
    borderColor: 'border-fuchsia-200',
    gradientFrom: 'from-fuchsia-500',
    gradientTo: 'to-pink-400',
  },
}

const TRIGGER_CONFIG: Record<ActionType, {
  label: string
  shortLabel: string
  description: string
  color: string
  bgColor: string
  borderColor: string
  dotColor: string
}> = {
  [ActionType.CREATE]: {
    label: 'When a record is created',
    shortLabel: 'On Create',
    description: 'Triggers immediately after a new record is saved',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    dotColor: 'bg-emerald-500',
  },
  [ActionType.UPDATE]: {
    label: 'When a record is updated',
    shortLabel: 'On Update',
    description: 'Triggers whenever any field on a record changes',
    color: 'text-primary-700',
    bgColor: 'bg-primary-50',
    borderColor: 'border-primary-200',
    dotColor: 'bg-primary-500',
  },
  [ActionType.DELETE]: {
    label: 'When a record is deleted',
    shortLabel: 'On Delete',
    description: 'Triggers when a record is moved to trash',
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    dotColor: 'bg-rose-500',
  },
  [ActionType.CALL_ENDED]: {
    label: 'When a call ends',
    shortLabel: 'Call ended',
    description: 'Triggers when a call on a record of this group ends',
    color: 'text-lime-700',
    bgColor: 'bg-lime-50',
    borderColor: 'border-lime-200',
    dotColor: 'bg-lime-500',
  },
  [ActionType.CALL_DISPOSITION_SET]: {
    label: 'When a call disposition is set',
    shortLabel: 'Call disposition',
    description: 'Triggers when an agent records how a call went',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    dotColor: 'bg-indigo-500',
  },
  [ActionType.TAG_ADDED]: {
    label: 'When a tag is added',
    shortLabel: 'Tag added',
    description: 'Triggers when a tag is added to a record, optionally only for chosen tags',
    color: 'text-fuchsia-700',
    bgColor: 'bg-fuchsia-50',
    borderColor: 'border-fuchsia-200',
    dotColor: 'bg-fuchsia-500',
  },
  [ActionType.TAG_REMOVED]: {
    label: 'When a tag is removed',
    shortLabel: 'Tag removed',
    description: 'Triggers when a tag is removed from a record, optionally only for chosen tags',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    dotColor: 'bg-orange-500',
  },
  [ActionType.NOTE_ADDED]: {
    label: 'When a note is added',
    shortLabel: 'Note added',
    description: 'Triggers when a person adds a note to a record (not notes written by automations)',
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    dotColor: 'bg-violet-500',
  },
  [ActionType.BUTTON_CLICKED]: {
    label: 'When a button is clicked',
    shortLabel: 'Button clicked',
    description: 'Triggers when a person clicks a button field on a record',
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    dotColor: 'bg-sky-500',
  },
}

const CALL_TRIGGERS: ActionType[] = [ActionType.CALL_ENDED, ActionType.CALL_DISPOSITION_SET]
const TAG_TRIGGERS: ActionType[] = [ActionType.TAG_ADDED, ActionType.TAG_REMOVED]

/** The filter parts a trigger accepts, the same ones the server keeps. */
function filterForTrigger(trigger: ActionType, filter: AutomationTriggerFilter): AutomationTriggerFilter | null {
  let kept: AutomationTriggerFilter = {}
  if (CALL_TRIGGERS.includes(trigger)) {
    kept = { outcomes: filter.outcomes, providerTypes: filter.providerTypes }
    if (trigger === ActionType.CALL_DISPOSITION_SET) kept.dispositionIds = filter.dispositionIds
  } else if (TAG_TRIGGERS.includes(trigger)) {
    kept = { tagIds: filter.tagIds }
  } else if (trigger === ActionType.UPDATE || trigger === ActionType.BUTTON_CLICKED) {
    kept = { fieldKeys: filter.fieldKeys }
  }
  const hasAny = Object.values(kept).some((list) => (list ?? []).length > 0)
  return hasAny ? kept : null
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildDefaultAction(type: AutomationActionType = AutomationActionType.ADD_NOTE): FormAction {
  if (type === AutomationActionType.SEND_NOTIFICATION) {
    // Starter text for a tag notification; recipients are left for the person to choose
    return {
      id: null,
      type,
      metadata: {
        ...defaultMetadataForType(type),
        title: '{{tag.label}}: {{record.title}}',
        body: '{{actor.name}} tagged {{record.title}} as {{tag.label}}.',
      },
    }
  }
  return { id: null, type, metadata: defaultMetadataForType(type) }
}

function defaultMetadataForType(type: AutomationActionType): ActionMetadata {
  switch (type) {
    case AutomationActionType.ADD_NOTE: return { note: '' }
    case AutomationActionType.SEND_EMAIL: return { to: '', subject: '', body: '' }
    case AutomationActionType.UPDATE_FIELD: return { fieldKey: '', value: '' }
    case AutomationActionType.SEND_NOTIFICATION:
      return { channels: ['IN_APP'], title: '', body: '', recipientRoleIds: [], recipientUserIds: [] }
  }
}

function validateForm(recordGroupId: string, actions: FormAction[]): FormErrors {
  const errors: FormErrors = {}
  if (!recordGroupId) errors.recordGroupId = 'Please select a record group'
  const actionErrors: FormErrors['actions'] = {}
  actions.forEach((action, i) => {
    const errs: { [field: string]: string } = {}
    if (action.type === AutomationActionType.ADD_NOTE) {
      if (!action.metadata.note?.trim()) errs.note = 'Note content is required'
    } else if (action.type === AutomationActionType.SEND_EMAIL) {
      // A reply takes its recipient and subject from the email it answers when they are left empty.
      if (!action.metadata.replyToField && !action.metadata.to?.trim()) errs.to = 'Recipient email is required'
      if (!action.metadata.replyToField && !action.metadata.subject?.trim()) errs.subject = 'Subject is required'
      if (action.metadata.sendFrom === 'WORKSPACE' && !action.metadata.sendFromConnectionId) errs.sendFromConnectionId = 'Choose a workspace mailbox'
      if (!action.metadata.body?.trim()) errs.body = 'Email body is required'
    } else if (action.type === AutomationActionType.UPDATE_FIELD) {
      if (!action.metadata.fieldKey?.trim()) errs.fieldKey = 'Field key is required'
      if (action.metadata.value === undefined || action.metadata.value === '') errs.value = 'Value is required'
    } else if (action.type === AutomationActionType.SEND_NOTIFICATION) {
      Object.assign(errs, validateSendNotification(action.metadata))
    }
    if (Object.keys(errs).length > 0) actionErrors[i] = errs
  })
  if (Object.keys(actionErrors).length > 0) errors.actions = actionErrors
  return errors
}

// ─── Metadata sub-forms ───────────────────────────────────────────────────────

function MetaField({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">
        {label} {required && <span className="text-rose-500 normal-case tracking-normal">*</span>}
      </Label>
      {children}
      {error && (
        <p className="flex items-center gap-1 text-xs text-rose-600">
          <AlertCircle className="h-3 w-3 shrink-0" /> {error}
        </p>
      )}
    </div>
  )
}

function AddNoteFields({ metadata, errors, onChange, recordGroupId }: { metadata: ActionMetadata; errors?: Record<string, string>; onChange: (m: ActionMetadata) => void; recordGroupId: string }) {
  return (
    <MetaField label="Note Content" required error={errors?.note}>
      <Textarea
        placeholder="Enter the note that will be added to the record…"
        value={metadata.note ?? ''}
        onChange={(e) => onChange({ ...metadata, note: e.target.value })}
        rows={3}
        className={errors?.note ? 'border-rose-300 focus:ring-rose-400' : ''}
      />
      <AvailableVariables recordGroupId={recordGroupId} />
    </MetaField>
  )
}

function SendEmailFields({ metadata, errors, onChange, recordGroupId }: { metadata: ActionMetadata; errors?: Record<string, string>; onChange: (m: ActionMetadata) => void; recordGroupId: string }) {
  return (
    <div className="space-y-3">
      {/* Template hint */}
      <div className="flex items-start gap-2 rounded-lg bg-primary-50 border border-primary-100 px-3 py-2.5">
        <Zap className="h-3.5 w-3.5 text-primary-500 shrink-0 mt-0.5" />
        <p className="text-[11px] text-primary-700 leading-relaxed">
          Use <code className="font-mono bg-primary-100 rounded px-1 py-px text-primary-800">{'{{field_key}}'}</code> to insert record data dynamically.
          Field keys are shown below each field label on the Record Detail page.
          Example: <code className="font-mono bg-primary-100 rounded px-1 py-px text-primary-800">{'{{email}}'}</code> or <code className="font-mono bg-primary-100 rounded px-1 py-px text-primary-800">{'{{name}}'}</code>.
        </p>
      </div>
      <AvailableVariables recordGroupId={recordGroupId} />
      <EmailDeliveryFields metadata={metadata} recordGroupId={recordGroupId} onChange={onChange} />
      {errors?.sendFromConnectionId && (
        <p className="flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3 shrink-0" /> {errors.sendFromConnectionId}</p>
      )}
      <MetaField label="To" required={!metadata.replyToField} error={errors?.to}>
        <Input type="text" placeholder={metadata.replyToField ? 'Leave empty to reply to the sender' : 'e.g. {{email}} or recipient@example.com'} value={metadata.to ?? ''} onChange={(e) => onChange({ ...metadata, to: e.target.value })} className={errors?.to ? 'border-rose-300' : ''} />
      </MetaField>
      <MetaField label="Subject" required={!metadata.replyToField} error={errors?.subject}>
        <Input placeholder={metadata.replyToField ? 'Leave empty to keep the original subject' : 'e.g. Hello {{name}}, your record was updated'} value={metadata.subject ?? ''} onChange={(e) => onChange({ ...metadata, subject: e.target.value })} className={errors?.subject ? 'border-rose-300' : ''} />
      </MetaField>
      <MetaField label="Body" required error={errors?.body}>
        <Textarea placeholder="e.g. Hi {{name}}, your status is now {{status}}." value={metadata.body ?? ''} onChange={(e) => onChange({ ...metadata, body: e.target.value })} rows={4} className={errors?.body ? 'border-rose-300' : ''} />
      </MetaField>
    </div>
  )
}

function UpdateFieldFields({ metadata, errors, onChange, recordGroupId }: { metadata: ActionMetadata; errors?: Record<string, string>; onChange: (m: ActionMetadata) => void; recordGroupId: string }) {
  return (
    <div className="space-y-3">
      {/* Template hint */}
      <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2.5">
        <Zap className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
        <p className="text-[11px] text-emerald-700 leading-relaxed">
          Enter the exact <span className="font-semibold">field key</span> of the field you want to update.
          Field keys are shown below each field label on the Record Detail page.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <MetaField label="Field Key" required error={errors?.fieldKey}>
          <Input placeholder="e.g. status" value={metadata.fieldKey ?? ''} onChange={(e) => onChange({ ...metadata, fieldKey: e.target.value })} className={errors?.fieldKey ? 'border-rose-300' : ''} />
        </MetaField>
        <MetaField label="Value" required error={errors?.value}>
          <Input placeholder="e.g. Approved" value={metadata.value ?? ''} onChange={(e) => onChange({ ...metadata, value: e.target.value })} className={errors?.value ? 'border-rose-300' : ''} />
        </MetaField>
      </div>
      <AvailableVariables recordGroupId={recordGroupId} />
    </div>
  )
}

/** Lists and flags of an action's metadata, as text (ids are counted, not shown). */
function formatMetaValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return value.every((v) => typeof v === 'string' && uuid.test(v))
      ? `${value.length} selected`
      : value.join(', ')
  }
  return String(value)
}

// ─── View: Action Card ────────────────────────────────────────────────────────

function ActionViewCard({ action, index, total }: { action: AutomationAction; index: number; total: number }) {
  const cfg = ACTION_CONFIG[action.type]
  const Icon = cfg.icon

  const metaEntries = Object.entries(action.metadata ?? {}).filter(([, v]) => v !== '' && v != null)

  return (
    <div className="relative flex gap-4">
      {/* connector line */}
      {index < total - 1 && (
        <div className="absolute left-5 top-12 bottom-0 w-px bg-gradient-to-b from-neutral-200 to-transparent" />
      )}

      {/* step circle */}
      <div className={cn(
        'relative z-10 h-10 w-10 rounded-full flex items-center justify-center shrink-0 shadow-sm',
        `bg-gradient-to-br ${cfg.gradientFrom} ${cfg.gradientTo}`
      )}>
        <Icon className="h-4.5 w-4.5 text-white" />
        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[9px] font-bold text-neutral-500">
          {index + 1}
        </span>
      </div>

      {/* card body */}
      <div className={cn('flex-1 rounded-xl border p-4 bg-white shadow-sm', cfg.borderColor)}>
        <div className="mb-3">
          <span className={cn('text-sm font-semibold', cfg.color)}>{cfg.label}</span>
        </div>

        {metaEntries.length > 0 ? (
          <dl className="space-y-2">
            {metaEntries.map(([key, val]) => (
              <div key={key} className="grid grid-cols-[6rem_1fr] gap-2 text-sm">
                <dt className="text-neutral-400 font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</dt>
                <dd className="text-neutral-700 font-medium break-all">{formatMetaValue(val)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-xs text-neutral-400 italic">No metadata configured</p>
        )}
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function AutomationDetailPage() {
  const { automationId } = useParams<{ automationId: string }>()
  const navigate = useNavigate()
  const isNew = !automationId || automationId === 'new'
  const automationActions = useAllowedActions('AUTOMATION', isNew ? null : automationId)
  const canToggle = automationActions.can(PERMISSION_KEYS.AUTOMATION_TOGGLE)
  const canUpdate = automationActions.can(PERMISSION_KEYS.AUTOMATION_UPDATE)

  const { automation, isLoading, error } = useAutomationDetail(automationId || 'new')
  const { recordGroups } = useRecordGroups()
  const {
    create: createAutomation,
    update: updateAutomation,
    toggle: toggleAutomation,
    isCreating,
    isUpdating,
    isToggling,
  } = useAutomations()

  const [isEditing, setIsEditing] = useState(isNew)
  // Shortcuts (e.g. a tag's "Notify when added") open a new automation with these preset
  const [prefill] = useState(() => readAutomationPrefill(window.location.search))
  const [recordGroupId, setRecordGroupId] = useState(prefill.recordGroupId)
  const [triggerEvent, setTriggerEvent] = useState<ActionType>(prefill.trigger ?? ActionType.CREATE)
  const [triggerFilter, setTriggerFilter] = useState<AutomationTriggerFilter>(
    prefill.tagId ? { tagIds: [prefill.tagId] } : {})
  const { data: callingMe } = useCallingMe()
  const isCallTrigger = CALL_TRIGGERS.includes(triggerEvent)
  const [actions, setActions] = useState<FormAction[]>(() => [buildDefaultAction(prefill.action)])
  const [errors, setErrors] = useState<FormErrors>({})
  const [successMessage, setSuccessMessage] = useState('')

  // Populate form from loaded automation
  useEffect(() => {
    if (automation) {
      setRecordGroupId(automation.recordGroupId)
      setTriggerEvent(automation.triggerEvent as ActionType)
      setTriggerFilter(automation.triggerFilter ?? {})
      setActions(
        automation.actions.map((a: AutomationAction) => ({
          id: a.id,
          type: a.type,
          metadata: (a.metadata as ActionMetadata) || {},
        }))
      )
    }
  }, [automation])

  // When navigating to "new", immediately open edit mode
  useEffect(() => {
    if (isNew) setIsEditing(true)
  }, [isNew])

  // ── Form handlers ──
  const handleAddAction = () => setActions([...actions, buildDefaultAction()])
  const handleRemoveAction = (i: number) => setActions(actions.filter((_, idx) => idx !== i))
  const handleActionChange = (i: number, updates: Partial<FormAction>) => {
    const next = [...actions]
    next[i] = { ...next[i], ...updates }
    setActions(next)
  }
  const handleActionTypeChange = (i: number, newType: AutomationActionType) => {
    const next = [...actions]
    next[i].type = newType
    next[i].metadata = defaultMetadataForType(newType)
    setActions(next)
  }

  const handleSubmit = () => {
    const validationErrors = validateForm(recordGroupId, actions)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      toast.error('Please fix validation errors')
      return
    }
    if (triggerEvent === ActionType.BUTTON_CLICKED && (triggerFilter.fieldKeys ?? []).length === 0) {
      toast.error('Choose the button that runs this automation')
      return
    }
    setErrors({})

    const payload: UpdateAutomationRequest = {
      recordGroupId,
      triggerEvent,
      triggerFilter: filterForTrigger(triggerEvent, triggerFilter),
      actions: actions.map((a) => ({
        type: a.type,
        metadata: a.metadata as Record<string, unknown>,
      })),
    }

    if (!isNew && automationId) {
      updateAutomation(
        { id: automationId, data: payload },
        {
          onSuccess: () => {
            setSuccessMessage('Automation updated successfully')
            toast.success('Automation updated')
            setIsEditing(false)
          },
          onError: (err: unknown) => {
            toast.error(apiErrorDetail(err) || 'Failed to update automation')
          },
        }
      )
    } else {
      createAutomation(payload, {
        onSuccess: (data) => {
          setSuccessMessage('Automation created successfully')
          toast.success('Automation created')
          setTimeout(() => navigate(`/automations/${data.id}`), 800)
        },
        onError: (err: unknown) => {
          toast.error(apiErrorDetail(err) || 'Failed to create automation')
        },
      })
    }
  }

  const handleToggle = () => {
    if (!automation) return
    toggleAutomation(
      { id: automation.id, enabled: !automation.enabled },
      {
        onSuccess: () => toast.success(`Automation ${automation.enabled ? 'disabled' : 'enabled'}`),
        onError: () => toast.error('Failed to toggle automation'),
      }
    )
  }

  const getRecordGroupName = (id: string) =>
    recordGroups.find((rg) => rg.id === id)?.name || 'Unknown Group'

  // ── Loading skeleton ──
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Hero skeleton */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 to-indigo-700 p-8">
          <Skeleton className="h-8 w-48 bg-white/20 rounded-xl mb-3" />
          <Skeleton className="h-5 w-64 bg-white/15 rounded-lg mb-6" />
          <div className="flex gap-3">
            <Skeleton className="h-8 w-24 bg-white/20 rounded-full" />
            <Skeleton className="h-8 w-32 bg-white/20 rounded-full" />
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <Skeleton className="h-48 rounded-xl" />
      </div>
    )
  }

  // ── Trigger config for the current automation ──
  const triggerCfg = automation ? TRIGGER_CONFIG[automation.triggerEvent as ActionType] : TRIGGER_CONFIG[ActionType.CREATE]
  const recordGroupName = automation ? getRecordGroupName(automation.recordGroupId) : ''

  // ─────────────────────────────────────────────────────────────────────────────
  // VIEW MODE
  // ─────────────────────────────────────────────────────────────────────────────
  if (!isNew && automation && !isEditing) {
    return (
      <div className="space-y-6">

        {/* ── Hero Header ── */}
        <div className={cn(
          'relative overflow-hidden rounded-2xl p-8 shadow-lg',
          automation.enabled
            ? 'bg-gradient-to-br from-primary-600 via-primary-700 to-indigo-700'
            : 'bg-gradient-to-br from-neutral-600 via-neutral-700 to-neutral-800'
        )}>
          {/* Background decoration */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -top-12 -right-12 h-56 w-56 rounded-full bg-white/5 blur-2xl" />
            <div className="absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-white/5 blur-xl" />
            <div className="absolute top-1/2 left-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/3 blur-3xl" />
          </div>

          <div className="relative">
            {/* Breadcrumb */}
            <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
              <button onClick={() => navigate('/automations')} className="hover:text-white transition-colors">
                Automations
              </button>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-white/90">Details</span>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-3">
                {/* Title */}
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-lg">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white leading-tight">{recordGroupName}</h1>
                    <p className="text-white/70 text-sm mt-0.5">
                      {automation.actions.length} action{automation.actions.length !== 1 ? 's' : ''} configured
                    </p>
                  </div>
                </div>

                {/* Trigger badge */}
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 px-3 py-1 text-xs font-semibold text-white">
                    <span className={cn('h-1.5 w-1.5 rounded-full', automation.enabled ? 'bg-emerald-400' : 'bg-neutral-400')} />
                    {automation.enabled ? 'Active' : 'Disabled'}
                  </span>
                  <span className={cn(
                    'inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 px-3 py-1 text-xs font-semibold text-white'
                  )}>
                    <Zap className="h-3 w-3" />
                    {triggerCfg.shortLabel}
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 shrink-0">
                {canToggle && (
                <button
                  onClick={handleToggle}
                  disabled={isToggling}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all shadow-sm',
                    automation.enabled
                      ? 'bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-sm'
                      : 'bg-emerald-500 hover:bg-emerald-400 text-white shadow-emerald-900/20'
                  )}
                >
                  {isToggling ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : automation.enabled ? (
                    <PowerOff className="h-4 w-4" />
                  ) : (
                    <Power className="h-4 w-4" />
                  )}
                  {automation.enabled ? 'Disable' : 'Enable'}
                </button>
                )}
                {canUpdate && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50 transition-all shadow-sm"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="grid gap-4 sm:grid-cols-3">
          {/* Trigger */}
          <Card className={cn('border-2', triggerCfg.borderColor, triggerCfg.bgColor)}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Trigger</span>
                <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center', triggerCfg.bgColor, 'border', triggerCfg.borderColor)}>
                  <Zap className={cn('h-3.5 w-3.5', triggerCfg.color)} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className={cn('text-base font-bold leading-snug', triggerCfg.color)}>{triggerCfg.shortLabel}</p>
              <p className="text-xs text-neutral-500 mt-1">{triggerCfg.description}</p>
            </CardContent>
          </Card>

          {/* Record Group */}
          <Card className="border-2 border-neutral-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Record Group</span>
                <div className="h-7 w-7 rounded-lg bg-neutral-100 border border-neutral-200 flex items-center justify-center">
                  <Settings2 className="h-3.5 w-3.5 text-neutral-500" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-base font-bold text-neutral-800 leading-snug truncate">{recordGroupName}</p>
              <p className="text-xs text-neutral-500 mt-1">Applied to this entity type</p>
            </CardContent>
          </Card>

          {/* Actions count */}
          <Card className="border-2 border-neutral-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Actions</span>
                <div className="h-7 w-7 rounded-lg bg-primary-50 border border-primary-200 flex items-center justify-center">
                  <Hash className="h-3.5 w-3.5 text-primary-600" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-base font-bold text-neutral-800 leading-snug">
                {automation.actions.length} action{automation.actions.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-neutral-500 mt-1">Execute in sequence</p>
            </CardContent>
          </Card>
        </div>

        {/* ── How it works flow ── */}
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 bg-neutral-50/60">
            <h2 className="text-sm font-bold text-neutral-800">Automation Flow</h2>
            <p className="text-xs text-neutral-500 mt-0.5">What happens when the trigger fires</p>
          </div>

          <div className="p-6 space-y-1">
            {/* Trigger node */}
            <div className="relative flex gap-4">
              {automation.actions.length > 0 && (
                <div className="absolute left-5 top-12 bottom-0 w-px bg-gradient-to-b from-neutral-200 to-transparent" />
              )}
              <div className={cn(
                'relative z-10 h-10 w-10 rounded-full flex items-center justify-center shrink-0 shadow-sm border-2',
                triggerCfg.bgColor, triggerCfg.borderColor
              )}>
                <Zap className={cn('h-4 w-4', triggerCfg.color)} />
              </div>
              <div className={cn('flex-1 rounded-xl border p-4', triggerCfg.bgColor, triggerCfg.borderColor)}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={cn('text-xs font-bold uppercase tracking-widest', triggerCfg.color)}>Trigger</span>
                  <span className="text-neutral-300">·</span>
                  <span className="text-xs text-neutral-500">{recordGroupName}</span>
                </div>
                <p className={cn('text-sm font-semibold', triggerCfg.color)}>{triggerCfg.label}</p>
              </div>
            </div>

            {/* Spacer between trigger and actions */}
            {automation.actions.length > 0 && <div className="ml-5 pl-[1px] py-1" />}

            {/* Action nodes */}
            <div className="space-y-4">
              {automation.actions.map((action, i) => (
                <ActionViewCard
                  key={action.id ?? i}
                  action={action}
                  index={i}
                  total={automation.actions.length}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // EDIT / CREATE MODE
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => (isNew ? navigate('/automations') : setIsEditing(false))}
            className="h-9 w-9 rounded-xl border border-neutral-200 bg-white flex items-center justify-center hover:bg-neutral-50 transition-colors shadow-sm"
          >
            <ArrowLeft className="h-4 w-4 text-neutral-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              {isNew ? 'New Automation' : 'Edit Automation'}
            </h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              {isNew ? 'Configure trigger and actions' : `Editing · ${recordGroupName}`}
            </p>
          </div>
        </div>
        <Badge variant={isNew ? 'default' : 'secondary'} className="text-xs">
          {isNew ? 'New' : 'Editing'}
        </Badge>
      </div>

      {/* Alerts */}
      {successMessage && (
        <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <p className="text-sm font-medium text-emerald-700">{successMessage}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 rounded-xl bg-rose-50 border border-rose-200 p-4">
          <AlertCircle className="h-5 w-5 text-rose-600 shrink-0" />
          <p className="text-sm font-medium text-rose-700">Failed to load automation details</p>
        </div>
      )}

      {/* ── Config Card ── */}
      <Card className="border-neutral-200 shadow-sm">
        <CardHeader className="border-b border-neutral-100 bg-neutral-50/60">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <Settings2 className="h-4 w-4 text-primary-600" />
            </div>
            <div>
              <CardTitle className="text-sm font-bold text-neutral-800">Configuration</CardTitle>
              <p className="text-xs text-neutral-500">Set the record group and trigger event</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Record Group */}
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500">
              Record Group <span className="text-rose-500 normal-case tracking-normal font-normal">*</span>
            </Label>
            <Select value={recordGroupId} onValueChange={setRecordGroupId}>
              <SelectTrigger className={cn('h-11 rounded-xl', errors.recordGroupId ? 'border-rose-300' : '')}>
                <SelectValue placeholder="Select a record group…" />
              </SelectTrigger>
              <SelectContent>
                {recordGroups.map((rg) => (
                  <SelectItem key={rg.id} value={rg.id}>{rg.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.recordGroupId && (
              <p className="flex items-center gap-1 text-xs text-rose-600">
                <AlertCircle className="h-3 w-3" /> {errors.recordGroupId}
              </p>
            )}
          </div>

          <Separator />

          {/* Trigger selection */}
          <div className="space-y-3">
            <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500">
              Trigger Event <span className="text-rose-500 normal-case tracking-normal font-normal">*</span>
            </Label>
            <div className="grid gap-3 sm:grid-cols-3">
              {(Object.entries(TRIGGER_CONFIG) as [ActionType, typeof TRIGGER_CONFIG[ActionType]][])
                // Call triggers only where calling is on the plan (or already in use here).
                .filter(([trigger]) => !CALL_TRIGGERS.includes(trigger) || callingMe?.featureEnabled || trigger === triggerEvent)
                .map(([trigger, cfg]) => (
                <button
                  key={trigger}
                  type="button"
                  onClick={() => setTriggerEvent(trigger)}
                  className={cn(
                    'relative rounded-xl border-2 p-4 text-left transition-all',
                    triggerEvent === trigger
                      ? `${cfg.borderColor} ${cfg.bgColor}`
                      : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
                  )}
                >
                  {triggerEvent === trigger && (
                    <span className="absolute top-2 right-2 h-4 w-4 rounded-full bg-current flex items-center justify-center">
                      <CheckCircle2 className={cn('h-3.5 w-3.5', cfg.color)} />
                    </span>
                  )}
                  <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center mb-2', cfg.bgColor, 'border', cfg.borderColor)}>
                    <Zap className={cn('h-3.5 w-3.5', cfg.color)} />
                  </div>
                  <p className={cn('text-sm font-semibold leading-snug', triggerEvent === trigger ? cfg.color : 'text-neutral-700')}>
                    {cfg.shortLabel}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1 leading-snug">{cfg.description}</p>
                </button>
              ))}
            </div>
            {isCallTrigger && (
              <CallTriggerFilterPicker trigger={triggerEvent} value={triggerFilter} onChange={setTriggerFilter} />
            )}
            {TAG_TRIGGERS.includes(triggerEvent) && (
              <TagTriggerFilterPicker recordGroupId={recordGroupId} value={triggerFilter} onChange={setTriggerFilter} />
            )}
            {triggerEvent === ActionType.BUTTON_CLICKED && (
              <ButtonTriggerFilterPicker recordGroupId={recordGroupId} value={triggerFilter} onChange={setTriggerFilter} />
            )}
            {triggerEvent === ActionType.UPDATE && (
              <FieldTriggerFilterPicker recordGroupId={recordGroupId} value={triggerFilter} onChange={setTriggerFilter} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Actions ── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-neutral-800">Actions</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Define what happens when the trigger fires</p>
          </div>
          <Button onClick={handleAddAction} variant="outline" size="sm" className="gap-2 h-8 rounded-xl text-xs">
            <Plus className="h-3.5 w-3.5" /> Add Action
          </Button>
        </div>

        <div className="space-y-3">
          {actions.map((action, index) => {
            const cfg = ACTION_CONFIG[action.type]
            const Icon = cfg.icon
            const actionErrors = errors.actions?.[index]

            return (
              <Card key={index} className={cn('border-2 shadow-sm transition-all', cfg.borderColor)}>
                <CardHeader className={cn('pb-3 rounded-t-xl', cfg.bgColor, 'border-b', cfg.borderColor)}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'h-8 w-8 rounded-lg flex items-center justify-center shadow-sm',
                        `bg-gradient-to-br ${cfg.gradientFrom} ${cfg.gradientTo}`
                      )}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <Select value={action.type} onValueChange={(v) => handleActionTypeChange(index, v as AutomationActionType)}>
                          <SelectTrigger className="h-auto border-0 bg-transparent p-0 text-sm font-bold shadow-none focus:ring-0 w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ACTION_CONFIG).map(([type, c]) => (
                              <SelectItem key={type} value={type}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-neutral-500 mt-px">Step {index + 1}</p>
                      </div>
                    </div>
                    {actions.length > 1 && (
                      <button
                        onClick={() => handleRemoveAction(index)}
                        className="h-7 w-7 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 flex items-center justify-center text-rose-600 transition-colors ml-auto"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="pt-5 space-y-4">
                  {/* Metadata fields */}
                  {action.type === AutomationActionType.ADD_NOTE && (
                    <AddNoteFields recordGroupId={recordGroupId} metadata={action.metadata} errors={actionErrors} onChange={(m) => handleActionChange(index, { metadata: m })} />
                  )}
                  {action.type === AutomationActionType.SEND_EMAIL && (
                    <SendEmailFields recordGroupId={recordGroupId} metadata={action.metadata} errors={actionErrors} onChange={(m) => handleActionChange(index, { metadata: m })} />
                  )}
                  {action.type === AutomationActionType.SEND_NOTIFICATION && (
                    <SendNotificationFields recordGroupId={recordGroupId} metadata={action.metadata} errors={actionErrors} onChange={(m) => handleActionChange(index, { metadata: m })} />
                  )}
                  {action.type === AutomationActionType.UPDATE_FIELD && (
                    <UpdateFieldFields recordGroupId={recordGroupId} metadata={action.metadata} errors={actionErrors} onChange={(m) => handleActionChange(index, { metadata: m })} />
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {/* ── Submit bar ── */}
      <div className="flex items-center justify-between gap-3 sticky bottom-0 -mx-4 -mb-4 lg:-mx-6 lg:-mb-6 px-6 py-4 bg-white/90 backdrop-blur-sm border-t border-neutral-200 shadow-lg">
        <button
          onClick={() => (isNew ? navigate('/automations') : setIsEditing(false))}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-xl border border-neutral-200 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isCreating || isUpdating}
          className="inline-flex items-center gap-2 h-9 px-6 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-sm font-semibold text-white transition-colors shadow-sm"
        >
          {isCreating || isUpdating ? (
            <><RefreshCw className="h-4 w-4 animate-spin" /> Saving…</>
          ) : (
            <><Save className="h-4 w-4" /> {isNew ? 'Create Automation' : 'Save Changes'}</>
          )}
        </button>
      </div>
    </div>
  )
}
